import { computed, Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { AuthService as ApiAuthService } from '../../../api/api/auth.service';
import { LoginResponseDto } from '../../../api/model/loginResponseDto';
import { User as ApiUser } from '../../../api/model/user';
import { AuthState, LoginCredentials, User, UserRole } from '../models/user.model';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly apiAuthService = inject(ApiAuthService);
  private readonly AUTH_STORAGE_KEY = 'scalex_auth';

  // Auth state using signals
  private readonly authState = signal<AuthState>(this.loadAuthStateFromStorage());

  // Computed signals for reactive state
  readonly currentUser = computed(() => this.authState().user);
  readonly isAuthenticated = computed(() => this.authState().isAuthenticated);
  readonly userRole = computed(() => this.authState().user?.role ?? null);

  constructor() {
    this.verifyAuth();
  }

  /**
   * Verify authentication state with API
   */
  verifyAuth(): void {
    const state = this.authState();
    if (state.token) {
      this.apiAuthService.authControllerMeV1().subscribe({
        next: (apiUser) => {
          const coreUser = this.mapApiUserToCoreUser(apiUser);
          this.setAuthenticatedUser(coreUser, state.token!);
        },
        error: (err) => {
          console.error('Session verification failed:', err);
          this.logout(false); // don't navigate, just clear state if session invalid
        }
      });
    }
  }

  /**
   * Login with email and password
   */
  login(credentials: LoginCredentials): Observable<User> {
    const { email, password } = credentials;

    return this.apiAuthService.authControllerLoginV1({ email, password }).pipe(
      map((response: LoginResponseDto) => {
        const coreUser = this.mapApiUserToCoreUser(response.user);
        this.setAuthenticatedUser(coreUser, response.token);
        return coreUser;
      }),
      catchError((error) => {
        console.error('Login failed', error);
        return throwError(() => new Error('Invalid email or password'));
      })
    );
  }

  /**
   * Update user profile
   */
  updateProfile(data: Partial<User>): Observable<User> {
    return this.apiAuthService.authControllerUpdateV1(data).pipe(
      map((apiUser) => {
        const coreUser = this.mapApiUserToCoreUser(apiUser);
        // Update local state with new user data but keep existing token
        const currentState = this.authState();
        if (currentState.token) {
          this.setAuthenticatedUser(coreUser, currentState.token);
        }
        return coreUser;
      })
    );
  }

  private mapApiUserToCoreUser(apiUser: ApiUser): User {
    console.log('[AuthService] Mapping API User:', apiUser);
    return {
      id: apiUser.id.toString(),
      username: apiUser.username || apiUser.email.split('@')[0],
      email: apiUser.email,
      role: (apiUser.appRole as unknown as UserRole) || UserRole.CLIENT, // Default fallback if role missing
      name:
        apiUser.name ||
        `${apiUser.firstName || ''} ${apiUser.lastName || ''}`.trim() ||
        'Unknown User',
      avatar: apiUser.avatar || '8.jpg'
      // Map other fields if necessary
    };
  }

  /**
   * Set authenticated user
   */
  setAuthenticatedUser(user: User, token: string): void {
    const newState: AuthState = {
      user,
      isAuthenticated: true,
      token
    };
    this.authState.set(newState);
    this.saveAuthStateToStorage(newState);

    // Also update UserService signal to reflect current user if needed,
    // although AuthUser is usually separate.
    // this.userService.updateCurrentUser(user);
  }

  /**
   * Logout current user
   */
  logout(navigate: boolean = true): void {
    // Call API logout if token exists
    const token = this.authState().token;
    if (token) {
      this.apiAuthService.authControllerLogoutV1().subscribe({
        error: (err) => console.warn('Logout API failed', err)
      });
    }

    this.authState.set({
      user: null,
      isAuthenticated: false
    });
    this.clearAuthStateFromStorage();

    if (navigate) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: UserRole): boolean {
    return this.userRole() === role;
  }

  /**
   * Check if user has any of the provided roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    const currentRole = this.userRole();
    return currentRole !== null && roles.includes(currentRole);
  }

  /**
   * Check if user can manage users (Super Admin only)
   */
  canManageUsers(): boolean {
    return this.hasRole(UserRole.SUPER_ADMIN);
  }

  /**
   * Check if user can add consultants (Super Admin only)
   */
  canAddConsultants(): boolean {
    return this.hasRole(UserRole.SUPER_ADMIN);
  }

  /**
   * Check if user can add contractors (Super Admin only)
   */
  canAddContractors(): boolean {
    return this.hasRole(UserRole.SUPER_ADMIN);
  }

  /**
   * Check if user can manage projects (Consultant only)
   */
  canManageProjects(): boolean {
    return this.hasRole(UserRole.CONSULTANT);
  }

  /**
   * Check if user can access marketplace (Consultant only)
   */
  canAccessMarketplace(): boolean {
    return this.hasRole(UserRole.CONSULTANT);
  }

  /**
   * Check if user can add clients (Consultant only)
   */
  canAddClients(): boolean {
    return this.hasRole(UserRole.CONSULTANT);
  }

  /**
   * Check if user can access client portal (Client only)
   */
  canAccessClientPortal(): boolean {
    return this.hasRole(UserRole.CLIENT);
  }

  /**
   * Check if user can manage contractor profile (Contractor only)
   */
  canManageContractorProfile(): boolean {
    return this.hasRole(UserRole.CONTRACTOR);
  }

  /**
   * Check if user can view assigned projects (Contractor only)
   */
  canViewAssignedProjects(): boolean {
    return this.hasRole(UserRole.CONTRACTOR);
  }

  /**
   * Load auth state from localStorage
   */
  private loadAuthStateFromStorage(): AuthState {
    try {
      const stored = localStorage.getItem(this.AUTH_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.token) {
          // Return stored state optimistically.
          // Constructor calls verifyAuth() to validate/update it.
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading auth state from storage:', error);
    }
    return { user: null, isAuthenticated: false };
  }

  /**
   * Save auth state to localStorage
   */
  private saveAuthStateToStorage(state: AuthState): void {
    try {
      localStorage.setItem(this.AUTH_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving auth state to storage:', error);
    }
  }

  /**
   * Clear auth state from localStorage
   */
  private clearAuthStateFromStorage(): void {
    try {
      localStorage.removeItem(this.AUTH_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing auth state from storage:', error);
    }
  }
}
